import { useEffect, useRef, useState } from "react";
import { submitContactForm } from "@/utils/apiClient";
import { siteData } from "@/store/siteData";

export default function ContactSection() {
  const [visible, setVisible] = useState(false);
  const settings = siteData.getGeneralSettings();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const fd = new FormData(e.currentTarget);
    try {
      await submitContactForm({
        ad_soyad: fd.get("ad_soyad")?.toString() ?? "",
        email:    fd.get("email")?.toString()    ?? "",
        telefon:  fd.get("telefon")?.toString()  ?? "",
        konu:     fd.get("konu")?.toString()     ?? "",
        mesaj:    fd.get("mesaj")?.toString()    ?? "",
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Gönderim sırasında bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="iletisim" ref={ref} className="bg-[#F8FAFC] py-24 border-t border-[#E2E8F0]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
          {/* Left */}
          <div className={`transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"}`}>
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-12 bg-[#3B82F6]"></div>
              <span className="text-[#334155] text-xs font-semibold tracking-[0.3em] uppercase">İletişim</span>
            </div>
            <h2 className="text-[#0F172A] font-black uppercase leading-tight mb-6"
              style={{ fontSize: "clamp(26px, 3.5vw, 48px)", letterSpacing: "-0.02em" }}
            >
              Bizimle<br />
              <span className="text-[#64748B]">İletişime Geçin</span>
            </h2>
            <p className="text-[#64748B] text-sm leading-relaxed mb-10 max-w-md">
              BT altyapısı, siber güvenlik, bulut çözümleri veya teknik destek hakkında bilgi almak için formu doldurun. En kısa sürede uzman ekibimiz sizinle iletişime geçecektir.
            </p>

            <div className="space-y-5">
              {[
                { icon: "ri-map-pin-line", label: "Adres", value: settings.address },
                { icon: "ri-phone-line", label: "Telefon", value: settings.phone },
                { icon: "ri-mail-line", label: "E-posta", value: settings.email },
                { icon: "ri-time-line", label: "Çalışma Saatleri", value: "Pzt – Cum: 09:00 – 18:00" },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center border border-[#E2E8F0] bg-[#E2E8F0]/50 flex-shrink-0">
                    <i className={`${item.icon} text-[#3B82F6] text-base`}></i>
                  </div>
                  <div>
                    <p className="text-[#94A3B8] text-xs tracking-widest uppercase mb-1">{item.label}</p>
                    <p className="text-[#334155] text-sm">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Form */}
          <div className={`transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6"}`}>
            {submitted ? (
              <div className="h-full flex items-center justify-center border border-[#E2E8F0] p-12">
                <div className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center border border-[#3B82F6] mx-auto mb-5">
                    <i className="ri-check-line text-[#3B82F6] text-2xl"></i>
                  </div>
                  <h3 className="text-[#0F172A] font-bold text-xl mb-3">Mesajınız Alındı</h3>
                  <p className="text-[#64748B] text-sm leading-relaxed">
                    En kısa sürede uzman ekibimiz sizinle iletişime geçecektir.
                  </p>
                </div>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form-main"
                onSubmit={handleSubmit}
                className="border border-[#E2E8F0] p-8 space-y-5 bg-[#FFFFFF]"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#64748B] text-xs tracking-widest uppercase mb-2">Ad Soyad *</label>
                    <input
                      type="text"
                      name="ad_soyad"
                      required
                      placeholder="Ahmet Yılmaz"
                      className="w-full bg-[#F1F5F9] border border-[#E2E8F0] text-[#0F172A] text-sm px-4 py-3 focus:outline-none focus:border-[#3B82F6] transition-colors placeholder:text-[#94A3B8]"
                    />
                  </div>
                  <div>
                    <label className="block text-[#64748B] text-xs tracking-widest uppercase mb-2">Telefon *</label>
                    <input
                      type="tel"
                      name="telefon"
                      required
                      placeholder="+90 (5xx) xxx xx xx"
                      className="w-full bg-[#F1F5F9] border border-[#E2E8F0] text-[#0F172A] text-sm px-4 py-3 focus:outline-none focus:border-[#3B82F6] transition-colors placeholder:text-[#94A3B8]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[#64748B] text-xs tracking-widest uppercase mb-2">E-posta *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="ahmet@firma.com"
                    className="w-full bg-[#F1F5F9] border border-[#E2E8F0] text-[#0F172A] text-sm px-4 py-3 focus:outline-none focus:border-[#3B82F6] transition-colors placeholder:text-[#94A3B8]"
                  />
                </div>

                <div>
                  <label className="block text-[#64748B] text-xs tracking-widest uppercase mb-2">Konu</label>
                  <select
                    name="konu"
                    className="w-full bg-[#F1F5F9] border border-[#E2E8F0] text-[#334155] text-sm px-4 py-3 focus:outline-none focus:border-[#3B82F6] transition-colors cursor-pointer"
                  >
                    <option value="">Konu Seçiniz</option>
                    <option value="ag_altyapi">Ağ & Sistem Altyapısı</option>
                    <option value="siber_guvenlik">Siber Güvenlik</option>
                    <option value="bulut_cozumleri">Bulut Çözümleri</option>
                    <option value="teknik_destek">Teknik Destek & Servis</option>
                    <option value="diger">Diğer</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[#64748B] text-xs tracking-widest uppercase mb-2">Mesajınız</label>
                  <textarea
                    name="mesaj"
                    rows={4}
                    maxLength={500}
                    placeholder="Bize proje veya talebiniz hakkında kısaca bilgi verin..."
                    className="w-full bg-[#F1F5F9] border border-[#E2E8F0] text-[#0F172A] text-sm px-4 py-3 focus:outline-none focus:border-[#3B82F6] transition-colors placeholder:text-[#94A3B8] resize-none"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3">
                    <i className="ri-error-warning-line flex-shrink-0"></i>
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="whitespace-nowrap w-full py-4 bg-[#3B82F6] text-white text-sm font-bold tracking-widest uppercase hover:bg-[#64748B] transition-colors duration-300 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading && <i className="ri-loader-4-line animate-spin"></i>}
                  {loading ? "Gönderiliyor..." : "Formu Gönder"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
